import { debounce } from 'lodash';
import { waitDelay } from './utils/shared';
import { Menu } from './types/shared-types';

let searchBoxEl: HTMLInputElement | null = null;
let clearButtonEl: HTMLElement | null = null;
let asideEl: HTMLElement | null = null;
let originalMenuList: HTMLElement | null = null;
let resultMenuList: HTMLElement | null = null;
let resultMenuContainer: HTMLElement | null = null;
let noDataMessageEl: HTMLElement | null = null;
let menuItemMap: Menu[] = [];

function parseMenu(menuElement: Element, fromSub = false, parentText: string[]): Menu[] {
    const menuItems: Menu[] = [];
    let listItems: NodeListOf<Element> | null = null;
    if (fromSub) {
        listItems = menuElement.querySelectorAll(':scope > .menu-sub .menu-item');
    } else {
        listItems = menuElement.querySelectorAll(':scope > .menu-item');
    }

    (listItems || []).forEach((li) => {
        const anchor = li.querySelector(':scope > .menu-link') as HTMLElement;
        if (anchor) {
            const titleElement = anchor.querySelector('.menu-title') as HTMLElement;
            if (titleElement) {
                const menuTitle = titleElement?.textContent || ''
                const newParentTextArr = [...parentText];
                newParentTextArr.push(menuTitle);
                const menuItem: Menu = {
                    menuTitle,
                    titleElement,
                    parentText,
                    element: anchor,
                    children: parseMenu(li, true, newParentTextArr),
                };
                menuItems.push(menuItem);
            }
        }
    });
    return menuItems;
}

function getElements() {
    if (!searchBoxEl) {
        searchBoxEl = document.querySelector('#search-box [data-id="search-input"]');
    }
    if (!clearButtonEl) {
        clearButtonEl = document.querySelector('#search-box [data-id="clear-search-input"]');
    }
    if (!asideEl) {
        asideEl = document.querySelector('[data-el-id="aside-el"]');
    }

    if (!originalMenuList) {
        originalMenuList = document.querySelector('[data-el-id="aside-el"] [data-el-id="menu-list"]');
    }

    if (!resultMenuContainer) {
        resultMenuContainer = document.querySelector('[data-el-id="aside-el"] [data-el-id="menu-search-result-container"]');
    }

    if (!noDataMessageEl) {
        noDataMessageEl = document.querySelector('[data-el-id="aside-el"] [data-el-id="no-data-template"]');
    }

    return {
        searchBoxEl,
        clearButtonEl,
        asideEl,
        originalMenuList,
        resultMenuContainer,
        noDataMessageEl
    };
}

function isElValid(element: HTMLElement | null): element is HTMLElement {
    return element instanceof HTMLElement;
}

function searchMenu(menuItems: Menu[], searchText: string): Menu[] {
    const result: Menu[] = [];
    menuItems.forEach(menuItem => {
        if (menuItem.menuTitle.toLowerCase().includes(searchText.toLowerCase())) {
            result.push(menuItem);
        }
        const childResults = searchMenu(menuItem.children, searchText);
        result.push(...childResults);

    });
    return result;
}

function onSearchChange() {
    const { searchBoxEl, asideEl, resultMenuContainer, noDataMessageEl } = getElements();
    if (
        isElValid(searchBoxEl) &&
        isElValid(asideEl) &&
        isElValid(resultMenuContainer) &&
        isElValid(noDataMessageEl)
    ) {
        resultMenuContainer.innerHTML = '';
        if (!searchBoxEl.value.length) {
            asideEl.classList.remove('menu-search-on');
        } else {
            asideEl.classList.add('menu-search-on');
            prepareMenuItem();
            const result = searchMenu(menuItemMap, searchBoxEl.value);
            if (result.length === 0) {
                noDataMessageEl.classList.add('show-no-result');
            } else {
                noDataMessageEl.classList.remove('show-no-result');
                if (!resultMenuList) {
                    throw new Error('resultMenuList not found');
                }
                resultMenuContainer.appendChild(resultMenuList);
                result.forEach(menuItem => {
                    highlightText(menuItem.titleElement, searchBoxEl.value);
                    displayAllMenuParent(menuItem.element);
                    openAllParentAccordion(menuItem.element);
                });
                
                // Initialize accordion handlers AFTER all results are processed
                initAccordionHandlers();
            }
        }
    }
}

function displayAllMenuParent(element: HTMLElement | null) {
    let parent = element;
    while (parent) {
        parent.classList.add('show-result-item');
        parent = parent?.parentElement?.closest('.menu-item,.menu') || null;
    }
    const parentMenuItem = element?.closest('.menu-item');
    if (parentMenuItem) {
        parentMenuItem.querySelectorAll('.menu-item')?.forEach((el) => {
            el.classList.add('show-result-item');
        });
    }
}

/**
 * Initialize accordion click handlers using event delegation
 */
function initAccordionHandlers() {
    if (!resultMenuList) return;
    
    // Remove any existing listener first (use a named function stored on the element)
    const existingHandler = (resultMenuList as any)._accordionHandler;
    if (existingHandler) {
        resultMenuList.removeEventListener('click', existingHandler);
    }
    
    // Create and store the handler
    const handler = (event: Event) => {
        const target = event.target as HTMLElement;
        
        // Check if clicked on menu-link that has an arrow (accordion trigger)
        const menuLink = target.closest('.menu-link') as HTMLElement;
        if (!menuLink) return;
        
        // Check if this menu-link has an arrow (is an accordion)
        const hasArrow = menuLink.querySelector('.menu-arrow');
        if (!hasArrow) return;
        
        // Check if we clicked on an actual navigation link (has href)
        const isNavigationLink = menuLink.getAttribute('href') && 
                                  menuLink.getAttribute('href') !== '#' &&
                                  menuLink.getAttribute('href') !== 'javascript:;';
        
        // If it's a navigation link, don't toggle - let the browser navigate
        if (isNavigationLink && target.closest('a')) return;
        
        // Find the parent menu-item to toggle
        const menuItem = menuLink.closest('.menu-item');
        if (!menuItem) return;
        
        // Find the accordion container (could be the menu-item itself or a parent)
        const accordionItem = menuItem.closest('.menu-accordion') || menuItem;
        
        // Toggle the show class
        accordionItem.classList.toggle('show');
        
        // Also toggle the sub-menu visibility
        const subMenu = accordionItem.querySelector(':scope > .menu-sub, :scope > .menu-item > .menu-sub');
        if (subMenu) {
            subMenu.classList.toggle('show');
        }
        
        event.preventDefault();
        event.stopPropagation();
    };
    
    (resultMenuList as any)._accordionHandler = handler;
    resultMenuList.addEventListener('click', handler);
}

function openAllParentAccordion(element: HTMLElement) {
    let parent: Element | null = element.closest('.menu-sub-accordion,.menu-accordion');
    while (parent) {
        parent.classList.add('show', 'hover', 'show-result-item');
        const menuSub = parent.querySelector(':scope > .menu-sub');
        if (menuSub) {
            menuSub.classList.add('show');
        }
        
        parent = parent?.parentElement?.closest('.menu-sub-accordion,.menu-accordion') || null;
    }
}

function highlightText(element: HTMLElement, searchText: string) {
    const textContent = element.textContent || '';
    const lowerText = textContent.toLowerCase();
    const lowerSearch = searchText.toLowerCase();
    const index = lowerText.indexOf(lowerSearch);

    if (index >= 0) {
        const prefix = textContent.substring(0, index);
        const match = textContent.substring(index, index + searchText.length);
        const postfix = textContent.substring(index + searchText.length);
        element.innerHTML = `${escapeHtml(prefix)}<span class="search-highlight">${escapeHtml(match)}</span>${escapeHtml(postfix)}`;
    }
}

function escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

function onClearButtonClick() {
    const { searchBoxEl } = getElements();
    if (isElValid(searchBoxEl)) {
        searchBoxEl.value = '';
    }
    onSearchChange();
}

function prepareMenuItem() {
    const { originalMenuList } = getElements();
    if (isElValid(originalMenuList)) {
        resultMenuList = originalMenuList.cloneNode(true) as HTMLElement;
        resultMenuList.id = 'result-menu-list';
        resultMenuList.style.height = getResultBoxHeight();
        resultMenuList.querySelectorAll('.hover,.show,.active').forEach(el => {
            el.classList.remove('hover', 'show', 'active');
        });
        
        // Remove show-result-item from all items initially
        resultMenuList.querySelectorAll('.show-result-item').forEach(el => {
            el.classList.remove('show-result-item');
        });
        
        menuItemMap = parseMenu(resultMenuList as HTMLElement, false, []);
    }
}

function getResultBoxHeight() {
    const availableHight = (document.getElementById('kt_aside')?.getBoundingClientRect()?.height) || 0;
    let occupiedHeight = 0;
    document.querySelectorAll('#kt_aside_logo, #search-box, #kt_aside_footer').forEach(el => {
        const rect = el.getBoundingClientRect();
        occupiedHeight += (rect?.height || 0);
    });
    const resultBoxHeight = availableHight - occupiedHeight;
    if (resultBoxHeight > 0) {
        return `${resultBoxHeight}px`;
    } else {
        return '';
    }
}

function pageResizeListener() {
    const resultMenuList = document.getElementById('result-menu-list');
    if (resultMenuList) {
        resultMenuList.style.height = getResultBoxHeight();
    }
}

export function initMenuSearch() {
    const { searchBoxEl, clearButtonEl } = getElements();
    if (isElValid(searchBoxEl) && isElValid(clearButtonEl)) {
        searchBoxEl.addEventListener('input', debounce(onSearchChange, waitDelay.EVENT_DELAY));
        clearButtonEl.addEventListener('click', onClearButtonClick);
        searchBoxEl.addEventListener('drop', function (event) {
            event.preventDefault();
        });
        window.addEventListener('resize', debounce(pageResizeListener, waitDelay.EVENT_DELAY));
    }
}